import { useRef, useState, useEffect } from "react";

export function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

export function useCounter(target, active, duration = 1600) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(ease * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return count;
}

export function Fade({ children, delay = 0, style = {}, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* SmartImage — never shows a broken image. Falls back to picsum
   on the second failure, and to a coloured gradient on the third. */
export function SmartImage({ src, fallbackSeed, fallbackBg, alt = "", style = {}, className = "" }) {
  const [stage, setStage] = useState(0); // 0 = primary, 1 = picsum, 2 = gradient
  const handleError = () => setStage(s => Math.min(s + 1, 2));

  if (stage === 2) {
    return (
      <div
        className={className}
        style={{
          background: fallbackBg || "linear-gradient(135deg,#475569,#1e293b)",
          width: "100%", height: "100%",
          ...style,
        }}
        aria-label={alt}
      />
    );
  }

  const url =
    stage === 0
      ? src
      : `https://picsum.photos/seed/${encodeURIComponent(fallbackSeed || alt || "kuldiip")}/800/600`;

  return (
    <img
      src={url}
      alt={alt}
      onError={handleError}
      loading="lazy"
      className={className}
      style={{
        width: "100%", height: "100%", objectFit: "cover", display: "block",
        ...style,
      }}
    />
  );
}

export function PageHeader({ tag, title, subtitle, theme, image }) {
  return (
    <div style={{
      background: theme.heroBg,
      padding: "6rem 5vw 5rem",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}>
      {image && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(${theme.heroBg.includes("linear-gradient") ? "" : ""}rgba(0,0,0,0.55),rgba(0,0,0,0.55)),url(${image})`,
          backgroundSize: "cover", backgroundPosition: "center",
          opacity: 0.35,
        }} />
      )}
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{
          display: "inline-block", fontSize: "0.7rem", letterSpacing: "0.2em",
          textTransform: "uppercase", color: theme.accent, marginBottom: 14,
          fontWeight: 600, background: `${theme.accent}22`,
          padding: "0.35rem 1rem", borderRadius: 20,
        }}>{tag}</div>
        <h1 style={{
          fontFamily: "'Playfair Display',serif",
          fontSize: "clamp(2rem,4vw,3rem)", fontWeight: 700,
          color: theme.textOnHero, marginBottom: "1rem", marginTop: 10,
          lineHeight: 1.2,
        }}>{title}</h1>
        <p style={{
          fontSize: "1rem", color: theme.textOnHeroMuted,
          maxWidth: 560, margin: "0 auto", lineHeight: 1.8,
        }}>{subtitle}</p>
      </div>
    </div>
  );
}

export function SectionLabel({ children, theme }) {
  return (
    <div style={{
      display: "inline-block", fontSize: "0.7rem", letterSpacing: "0.2em",
      textTransform: "uppercase", color: theme.accent,
      fontWeight: 700, marginBottom: 12,
      background: `${theme.accent}18`, padding: "0.3rem 0.85rem", borderRadius: 20,
    }}>{children}</div>
  );
}

export function SectionTitle({ children, theme, center = false }) {
  return (
    <h2 style={{
      fontFamily: "'Playfair Display',serif",
      fontSize: "clamp(1.7rem,3vw,2.5rem)", fontWeight: 700,
      color: theme.textPrimary, lineHeight: 1.25, marginBottom: "1rem",
      textAlign: center ? "center" : "left",
    }}>{children}</h2>
  );
}

export function Card({ children, theme, style = {}, hover = true }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => hover && setHov(true)}
      onMouseLeave={() => hover && setHov(false)}
      style={{
        background: theme.cardBg,
        border: `1px solid ${hov ? theme.borderHover : theme.border}`,
        borderRadius: 14,
        boxShadow: hov ? theme.shadowHover : theme.shadowCard,
        transition: "all 0.3s ease",
        transform: hov && hover ? "translateY(-4px)" : "translateY(0)",
        ...style,
      }}>
      {children}
    </div>
  );
}

export function PrimaryButton({ children, onClick, href, theme, style = {} }) {
  const [hov, setHov] = useState(false);
  const props = {
    onMouseEnter: () => setHov(true),
    onMouseLeave: () => setHov(false),
    style: {
      display: "inline-flex", alignItems: "center", gap: 8,
      background: hov ? theme.accentHover : theme.accent,
      color: theme.textOnAccent,
      padding: "0.85rem 1.8rem", borderRadius: 8,
      fontSize: "0.88rem", fontWeight: 600,
      textDecoration: "none", border: "none", cursor: "pointer",
      fontFamily: "'DM Sans',sans-serif",
      boxShadow: `0 4px 18px ${theme.accent}45`,
      transition: "all 0.25s",
      transform: hov ? "translateY(-2px)" : "translateY(0)",
      ...style,
    },
  };
  if (href) return <a href={href} {...props}>{children}</a>;
  return <button onClick={onClick} {...props}>{children}</button>;
}

export function OutlineButton({ children, onClick, href, theme, style = {} }) {
  const [hov, setHov] = useState(false);
  const props = {
    onMouseEnter: () => setHov(true),
    onMouseLeave: () => setHov(false),
    style: {
      display: "inline-flex", alignItems: "center", gap: 8,
      background: hov ? "rgba(255,255,255,0.12)" : "transparent",
      color: theme.textOnHero,
      padding: "0.85rem 1.8rem", borderRadius: 8,
      fontSize: "0.88rem", fontWeight: 600,
      textDecoration: "none", border: "1px solid rgba(255,255,255,0.38)", cursor: "pointer",
      fontFamily: "'DM Sans',sans-serif",
      transition: "all 0.25s",
      transform: hov ? "translateY(-2px)" : "translateY(0)",
      ...style,
    },
  };
  if (href) return <a href={href} {...props}>{children}</a>;
  return <button onClick={onClick} {...props}>{children}</button>;
}
